<template>
  <li
    v-if="active"
    class="dropdown client-switcher-container"
  >
    <a
      href="#"
      class="dropdown-toggle"
      data-toggle="dropdown"
      role="button"
      aria-haspopup="true"
      aria-expanded="false"
    >
      <i
        class="fa fa-user-o"
        aria-hidden="true"
      />
      <span class="client-switcher-current">{{ selectedClientLabel }}</span>
      <span class="caret" />
    </a>
    <ul class="dropdown-menu client-switcher-menu">
      <li
        v-if="login"
        class="client-switcher-header"
      >
        <div>
          <span class="text-muted">Вы вошли как:</span> <strong>{{ login }}</strong>
        </div>
        <div v-if="member && member.name">
          <span class="text-muted">Сотрудник:</span> <strong>{{ member.name }}</strong>
        </div>
        <div v-if="clients.length">
          <span class="text-muted">Клиентов:</span> <strong>{{ clients.length }}</strong>
        </div>
      </li>
      <li
        v-if="clients.length > 1"
        class="client-switcher-search"
      >
        <input
          ref="searchInput"
          :value="search"
          type="text"
          class="form-control"
          placeholder="Поиск по юр.лицу, логину, домену или менеджеру"
          @input="onSearchInput"
          @click.stop
        >
      </li>
      <li
        v-if="loading"
        class="client-switcher-empty"
      >
        <span class="text-muted"><i
          class="fa fa-spinner fa-spin"
          aria-hidden="true"
        /> Загрузка...</span>
      </li>
      <li
        v-else-if="!clients.length"
        class="client-switcher-empty"
      >
        <span class="text-muted">У вас нет активных клиентов</span>
      </li>
      <li
        v-else-if="!filteredClients.length"
        class="client-switcher-empty"
      >
        <span class="text-muted">Ничего не найдено</span>
      </li>
      <template v-else>
        <li
          v-for="client in filteredClients"
          :key="client.login"
          :class="{ active: client.login === selectedClientLogin }"
        >
          <a
            href="#"
            @click.prevent="onSelect(client)"
          >
            <strong><span
              class="client-switcher-copyable"
              title="Скопировать"
              @click.stop.prevent="copyToClipboard(primaryLabel(client))"
            >{{ primaryLabel(client) }}<i
              class="fa fa-copy client-switcher-copy-icon"
              aria-hidden="true"
            /></span></strong>
            <small
              v-if="client.name"
              class="client-switcher-line"
            ><span
              class="client-switcher-copyable"
              title="Скопировать"
              @click.stop.prevent="copyToClipboard(client.name)"
            >{{ client.name }}<i
              class="fa fa-copy client-switcher-copy-icon"
              aria-hidden="true"
            /></span></small>
            <small
              v-if="client.login"
              class="client-switcher-line"
            ><span
              class="client-switcher-copyable"
              title="Скопировать"
              @click.stop.prevent="copyToClipboard(client.login)"
            >Логин: {{ client.login }}<i
              class="fa fa-copy client-switcher-copy-icon"
              aria-hidden="true"
            /></span></small>
            <small
              v-for="(manager, index) in managersOf(client)"
              :key="'manager-' + index"
              class="client-switcher-line client-switcher-manager"
              :title="managerTitle(manager)"
            >{{ managerLabel(manager) }}</small>
            <span
              v-if="client.services && client.services.length"
              class="client-switcher-services"
            >
              <a
                v-for="service in client.services"
                :key="service"
                href="#"
                class="client-switcher-service"
                :class="{ 'client-switcher-service--disabled': !isServiceSupported(service) }"
                :style="{ backgroundColor: serviceColor(service) }"
                :title="serviceTitle(service)"
                @click.stop.prevent="onServiceClick(client, service)"
              >{{ service }}</a>
            </span>
          </a>
        </li>
      </template>
    </ul>
  </li>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex'
import Notification from '../../notification'
import { isModuleEnabled } from '../../helper'

// Сопоставление имён услуг от common-auth (lowercase) с реализованными в ЛК модулями.
// Имя модуля используется для проверки isModuleEnabled (через VUE_APP_<NAME>_ENABLED).
// Услуги, отсутствующие в этом списке, считаются не реализованными.
const SUPPORTED_SERVICES = {
  seo: 'SEO',
  context: 'Context',
  serm: 'SERM'
}

// Подписи ролей, в которых менеджер закреплён за клиентом (roles из /api/impersonation/clients).
// Основная роль подписывается нейтрально, чтобы не шуметь в самом частом случае.
const MANAGER_ROLE_LABELS = {
  main: 'Менеджер',
  substitute: 'Замещающий менеджер',
  additional: 'Дополнительный менеджер'
}

export default {
  name: 'ClientSwitcher',
  computed: {
    ...mapState('impersonation', [
      'active',
      'clients',
      'selectedClientLogin',
      'search',
      'loading',
      'login',
      'member'
    ]),
    ...mapGetters('impersonation', ['filteredClients', 'selectedClient']),
    /**
     * Текст в кнопке переключателя: домены/имя/логин выбранного клиента, иначе статус
     * («Загрузка...», «Выберите клиента», «Нет клиентов»).
     */
    selectedClientLabel () {
      if (this.selectedClient) {
        return this.primaryLabel(this.selectedClient)
      }
      if (this.loading) {
        return 'Загрузка...'
      }
      return this.clients.length ? 'Выберите клиента' : 'Нет клиентов'
    }
  },
  methods: {
    ...mapActions('impersonation', {
      selectClientAction: 'selectClient',
      setSearchAction: 'setSearch'
    }),
    /**
     * Выбирает клиента. Если у него есть поддерживаемая в ЛК услуга — переходит в неё,
     * иначе перезагружает текущую страницу.
     */
    onSelect (client) {
      if (client.login === this.selectedClientLogin) {
        return
      }
      const firstSupported = this.firstSupportedService(client)
      if (firstSupported) {
        this.selectClientAction({ login: client.login, redirectTo: '/' + firstSupported })
      } else {
        this.selectClientAction(client.login)
      }
    },
    onSearchInput (event) {
      this.setSearchAction(event.target.value)
    },
    /**
     * Возвращает основную подпись клиента: домены через запятую, иначе имя, иначе логин.
     */
    primaryLabel (client) {
      if (client.domains && client.domains.length) {
        return client.domains.join(', ')
      }
      return client.name || client.login || ''
    },
    /**
     * Возвращает закреплённых за клиентом менеджеров, кроме самого текущего сотрудника: отсутствие
     * строки менеджера и означает, что клиент закреплён за ним самим. Пусто, если сервер менеджеров
     * не отдал (ответ из кэша предыдущей версии).
     */
    managersOf (client) {
      if (!Array.isArray(client.managers)) {
        return []
      }
      return client.managers.filter(manager => manager.relation !== 'self')
    },
    /**
     * Возвращает подпись менеджера: роль с именем (или id, если сотрудника уже нет в справочнике),
     * а в скобках — цепочку промежуточных руководителей от текущего сотрудника вниз до этого менеджера.
     */
    managerLabel (manager) {
      const name = manager.name || ('#' + manager.id)
      const chain = Array.isArray(manager.chain) ? manager.chain : []
      const role = MANAGER_ROLE_LABELS[manager.role] || MANAGER_ROLE_LABELS.main
      return role + ': ' + name + (chain.length ? ' (' + chain.join(' → ') + ')' : '')
    },
    /**
     * Возвращает подсказку с полным путём подчинения от текущего сотрудника до менеджера клиента.
     * Возвращает null для менеджера в прямом подчинении: там подсказка лишь повторяла бы строку.
     */
    managerTitle (manager) {
      const name = manager.name || ('#' + manager.id)
      const role = MANAGER_ROLE_LABELS[manager.role] || MANAGER_ROLE_LABELS.main
      if (manager.relation !== 'subordinate') {
        return role + ' вне вашей структуры: ' + name
      }
      const chain = Array.isArray(manager.chain) ? manager.chain : []
      if (!chain.length) {
        return null
      }
      return role + '\n' + ['Вы'].concat(chain, name).join(' → ')
    },
    /**
     * Возвращает цвет бейджа услуги.
     */
    serviceColor (service) {
      // Цвета совпадают с фоном пунктов соответствующих услуг в TheSidebar.vue.
      const colors = {
        seo: '#d71c3a',
        context: '#3db613',
        serm: '#0087f7'
      }
      return colors[String(service).toLowerCase()] || '#777'
    },
    /**
     * Обработчик клика по бейджу услуги: для текущего клиента — переход в услугу, для другого —
     * выбор клиента с переходом в услугу. Недоступные услуги игнорирует.
     */
    onServiceClick (client, service) {
      if (!this.isServiceSupported(service)) {
        return
      }
      const path = '/' + String(service).toLowerCase()
      if (this.selectedClientLogin === client.login) {
        window.location = path
        return
      }
      this.selectClientAction({ login: client.login, redirectTo: path })
    },
    /**
     * Реализована ли услуга в ЛК (есть в SUPPORTED_SERVICES и модуль включён).
     */
    isServiceSupported (service) {
      const moduleName = SUPPORTED_SERVICES[String(service).toLowerCase()]
      return !!moduleName && isModuleEnabled(moduleName)
    },
    /**
     * Возвращает первую поддерживаемую в ЛК услугу клиента (lowercase) или null.
     */
    firstSupportedService (client) {
      if (!Array.isArray(client.services)) {
        return null
      }
      for (const service of client.services) {
        if (this.isServiceSupported(service)) {
          return String(service).toLowerCase()
        }
      }
      return null
    },
    /**
     * Возвращает подсказку (title) для бейджа услуги.
     */
    serviceTitle (service) {
      return this.isServiceSupported(service)
        ? 'Перейти в ' + String(service).toUpperCase()
        : 'Услуга недоступна в ЛК'
    },
    /**
     * Копирует текст в буфер обмена (с fallback для не-secure контекста) и показывает уведомление.
     */
    copyToClipboard (text) {
      const value = String(text || '')
      if (!value) {
        return
      }
      const onSuccess = () => Notification.notifySuccess({
        title: 'Скопировано',
        content: value,
        duration: 2000
      })
      const onError = () => Notification.notifyError({
        title: 'Не удалось скопировать',
        content: 'Скопируйте текст вручную'
      })

      if (navigator.clipboard && window.isSecureContext) {
        navigator.clipboard.writeText(value).then(onSuccess).catch(onError)
        return
      }

      // Fallback для не-secure контекста (например, на dev по http).
      const textarea = document.createElement('textarea')
      textarea.value = value
      textarea.style.position = 'fixed'
      textarea.style.opacity = '0'
      document.body.appendChild(textarea)
      textarea.select()
      try {
        const ok = document.execCommand('copy')
        ok ? onSuccess() : onError()
      } catch (e) {
        onError()
      } finally {
        document.body.removeChild(textarea)
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.client-switcher-container {
  .client-switcher-current {
    display: inline-block;
    max-width: 200px;
    margin-left: 6px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    vertical-align: middle;
  }
}

.client-switcher-menu {
  min-width: 280px;
  max-width: 360px;
  max-height: 400px;
  overflow-y: auto;
}

.client-switcher-header {
  padding: 6px 12px;
  font-size: 12px;
  border-bottom: 1px solid #eee;
  margin-bottom: 4px;
}

.client-switcher-search {
  padding: 6px 12px;

  .form-control {
    width: 100%;
  }
}

.client-switcher-empty {
  padding: 6px 12px;
}

.client-switcher-line {
  display: block;
  font-size: 12px;
  line-height: 1.3;
  color: #777;
}

.client-switcher-menu > li.active > a .client-switcher-line {
  color: rgba(255, 255, 255, 0.85);
}

.client-switcher-manager {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.client-switcher-services {
  display: block;
  margin-top: 4px;
  line-height: 1.6;
}

.client-switcher-service {
  display: inline-block;
  margin-right: 4px;
  padding: 1px 6px;
  color: #fff;
  font-size: 11px;
  border-radius: 3px;
  text-transform: uppercase;
  text-decoration: none;
  cursor: pointer;

  &:hover,
  &:focus {
    color: #fff;
    text-decoration: none;
    filter: brightness(1.1);
  }

  &--disabled,
  &--disabled:hover,
  &--disabled:focus {
    cursor: default;
    opacity: 0.5;
    filter: none;
  }
}

.client-switcher-copyable {
  display: inline-block;
  max-width: 280px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  vertical-align: bottom;
  cursor: pointer;

  .client-switcher-copy-icon {
    margin-left: 6px;
    opacity: 0;
    transition: opacity 0.15s ease;
  }

  &:hover .client-switcher-copy-icon {
    opacity: 0.8;
  }
}
</style>
